import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Clock, Zap } from 'lucide-react';

const Support = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Pakete & Preise – Schteffann IT-Dienstleistungen Mönchengladbach',
    description: 'Transparente Preise für IT-Unterstützung in Mönchengladbach: Projekte, Begleitung, Support. Individuell auf KMU zugeschnitten.',
    url: 'https://schteffann.de/support'
  };

  const modelle = [
    {
      title: 'Projektarbeit',
      beschreibung: 'Für einmalige Vorhaben',
      geeignet: 'Microsoft 365 Einrichtung, Prozessdigitalisierung, Infrastruktur-Aufbau',
      ablauf: [
        'Kostenloses Erstgespräch & Bedarfsanalyse',
        'Festpreis-Angebot mit klarem Leistungsumfang',
        'Umsetzung in definierten Schritten',
        'Übergabe & Dokumentation'
      ],
      preis: 'Preis auf Anfrage'
    },
    {
      title: 'Laufende Begleitung',
      beschreibung: 'Für kontinuierliche Unterstützung',
      geeignet: 'Regelmäßige IT-Dienstleistungen, Optimierung, Ansprechpartner für Fragen',
      ablauf: [
        'Monatliches oder quartalsweises Kontingent',
        'Flexible Nutzung: Beratung, Schulung, kleine Anpassungen',
        'Regelmäßige Status-Gespräche',
        'Transparente Abrechnung'
      ],
      preis: 'Preis auf Anfrage'
    },
    {
      title: 'Interims-Unterstützung',
      beschreibung: 'Für temporäre Engpässe',
      geeignet: 'Urlaubs- oder Krankheitsvertretung, Projektphasen, Übergangszeiten',
      ablauf: [
        'Schneller Einstieg möglich',
        'Definierter Zeitraum oder bis zur Lösung',
        'Volle oder teilweise Übernahme',
        'Strukturierte Übergabe'
      ],
      preis: 'Preis auf Anfrage'
    }
  ];

  const fakten = [
    'Keine versteckten Kosten – Sie wissen vorher, was auf Sie zukommt',
    'Faire Stundensätze für KMU',
    'Anfahrt im Raum Mönchengladbach inklusive',
    'Flexible Zahlungsmodelle möglich',
    'Erste Beratung immer kostenfrei'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Pakete & Preise – IT-Dienstleistungen Mönchengladbach | Schteffann"
        description="Transparente Preise für IT-Unterstützung in Mönchengladbach: Projekte, Begleitung, Support. Individuell auf KMU zugeschnitten. Preise auf Anfrage."
        keywords="IT Preise Mönchengladbach, IT Support KMU, Projektarbeit IT, Interims IT, Schteffann Mönchengladbach"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              Pakete & Preise
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Transparente </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Zusammenarbeit</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Jedes Unternehmen ist anders. Deshalb gibt es keine Standardpreise. Ich erstelle Ihnen ein individuelles Angebot – fair, transparent, ohne Überraschungen.
            </p>
          </div>
        </div>
      </section>

      {/* Modelle */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Wie wir zusammenarbeiten können
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Drei gängige Modelle – je nachdem, was Sie brauchen
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {modelle.map((modell, index) => (
              <Card key={index} className="border-slate-200 shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{modell.title}</h3>
                    <p className="text-slate-600">{modell.beschreibung}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Geeignet für</h4>
                    <p className="text-slate-700">{modell.geeignet}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Ablauf</h4>
                    <div className="space-y-2">
                      {modell.ablauf.map((schritt, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-slate-600">{schritt}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-200">
                    <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                      {modell.preis}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fakten */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Was Sie erwarten können</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {fakten.map((fakt, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{fakt}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Warum kein Festpreis */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Warum keine Festpreise?</h2>
            <div className="text-lg text-slate-700 leading-relaxed space-y-4 text-left">
              <p>
                Jedes Unternehmen hat andere Voraussetzungen: Manche haben schon Microsoft 365, andere nicht. Manche brauchen nur eine Stunde Beratung, andere ein mehrwöchiges Projekt. Manche haben 3 Mitarbeiter, andere 30.
              </p>
              <p>
                Standardpreise würden entweder zu teuer oder zu günstig sein. Deshalb schaue ich mir erst Ihre Situation an und mache Ihnen dann ein passendes Angebot.
              </p>
              <p className="font-medium text-slate-900">
                Das Erstgespräch ist immer kostenlos. Sie wissen also vorher, was auf Sie zukommt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Lassen Sie uns über Ihr Projekt sprechen
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Im kostenlosen Erstgespräch schaue ich mir Ihre Situation an und sage Ihnen ehrlich, was ich tun kann – und was es ungefähr kosten würde.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Support;
